import moment from "moment"
import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { isError } from "react-query"
import { useDispatch } from "react-redux"
import { displayAlert } from "../../../../state/alert/reducer"
import { StatBoxFilters } from "../../../../state/listing/generic-listing"
import { QuoteStatBox } from "../../../../state/listing/generic-listing/column-definitions/quotes"
import SearchService from "../../../../state/search/SearchService"
import SearchTotals from "../../../../state/search/SearchTotals"
import QuoteStats, { QuoteStatsAttributes } from "../../../../state/stats/QuoteStats"
import StatsService from "../../../../state/stats/StatsService"
import { getFormattedDate, getLastMonth } from "../../../../utils/date"
import { AlertVariant } from "../../../alert/Alert"
import StatBox from "../../statbox"

interface OwnProps {
    selected: string,
    onChange: (
        statBox: string,
        filters: StatBoxFilters,
        previousFilters: StatBoxFilters,
    ) => void,
}

const QuotesStatBoxes = (props: OwnProps) => {
    const dispatch = useDispatch()
    const [wonAmount, setWonAmount] = useState<string>("0")
    const [wonCount, setWonCount] = useState<number>(0)
    const [stats, setStats] = useState<QuoteStatsAttributes>()

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([
                getSearchTotalsWon(),
                getStats(),
            ])
        }

        fetchData()
    }, [])
    
    const getStats = async () => {
        const stats = await StatsService.GetQuoteStats()

        if (isError(stats)) {
            dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load quote stats" }))
        } else {
            setStats((stats as QuoteStats).getAttributes())
        }
    }

    const getSearchTotalsWon = async () => {
        const postData = {
            keywords: "*",
            objects: ["quotes"],
            fields: [
                "identifier",
            ],
            totals: [
                "baseCurrencyTotalValueAmount",
            ],
            filters: {
                status: ["invoiced", "approved"],
                approvedAt: ">;" + getFormattedDate(moment().endOf("day").subtract(30, "days"))
            },
        }

        const res = await SearchService.mainSearchTotals(postData)

        if (isError(res)) {
            dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load search totals" }))
        } else {
            setWonAmount((res as SearchTotals).getAttributes().totals.baseCurrencyTotalValueAmount)
            setWonCount((res as SearchTotals).getAttributes().totalItemsCount)
        }
    }
    
    const getFilterForStatBox = (id: string): StatBoxFilters => {
        switch (id) {
        case QuoteStatBox.DRAFT:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["draft", "new"],
                    op: "isOneOf",
                    label: "Draft, New"
                }
            }
        case QuoteStatBox.PROPOSED:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["proposed"],
                    op: "isOneOf",
                    label: "Proposed"
                }
            }
        case QuoteStatBox.ACCEPTED:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["pre-approved"],
                    op: "isOneOf",
                    label: "Pre approved"
                }
            }
        case QuoteStatBox.WON:
            return {
                "statuses": {
                    nameId: "statuses",
                    value: ["approved", "invoiced"],
                    op: "isOneOf",
                    label: "Approved, Invoiced"
                },
                "approvedAt": {
                    nameId: "approvedAt",
                    value: { startDate: getLastMonth() },
                    op: "greaterThan",
                    label: `${moment(getLastMonth()).format("MMMM Do YYYY")}`,
                }
            }
        default:
            throw new Error("Error: Invalid stat box type for filters")
        }
    }

    const handleChange = (statBoxId: string) => {
        props.onChange(
            statBoxId,
            statBoxId ? getFilterForStatBox(statBoxId) : {},
            props.selected ? getFilterForStatBox(props.selected) : {},
        )
    }

    return (
        <>
            <StatBox
                id={QuoteStatBox.DRAFT}
                selected={props.selected === QuoteStatBox.DRAFT}
                count={(stats?.newCount ?? 0) + (stats?.draftCount ?? 0)}
                amount={(parseFloat(stats?.newAmount ?? "0") + parseFloat(stats?.draftAmount ?? "0"))}
                label={<FormattedMessage
                    id="quote.stat-box.draft"
                    defaultMessage="Draft"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={QuoteStatBox.PROPOSED}
                selected={props.selected === QuoteStatBox.PROPOSED}
                count={stats?.proposedCount ?? 0}
                amount={stats?.proposedAmount ?? "0"}
                label={<FormattedMessage
                    id="quote.stat-box.proposed"
                    defaultMessage="Proposed"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={QuoteStatBox.ACCEPTED}
                className="warning-box"
                activeClassName="active-warning-box"
                selected={props.selected === QuoteStatBox.ACCEPTED}
                count={stats?.preApprovedCount ?? 0}
                amount={stats?.preApprovedAmount ?? "0"}
                label={<FormattedMessage
                    id="quote.stat-box.accepted"
                    defaultMessage="Accepted"
                />}
                onChange={handleChange}
            />
            <StatBox
                id={QuoteStatBox.WON}
                className="success-box"
                activeClassName="active-success-box"
                selected={props.selected === QuoteStatBox.WON}
                count={wonCount ?? 0}
                amount={wonAmount ?? "0"}
                label={<FormattedMessage
                    id="quote.stat-box.won"
                    defaultMessage="Won last 30 days"
                />}
                onChange={handleChange}
            />
        </>
    )
}

export default injectIntl(QuotesStatBoxes)


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/stat-boxes/QuotesStatBoxes.tsx